import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Target, CheckCircle, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssessmentPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadMsgIndex, setLoadMsgIndex] = useState(0);
  const progressTimerRef = useRef(null);
  const msgTimerRef = useRef(null);
  const [formData, setFormData] = useState({
    education_level: '',
    board_stream: '',
    subjects: [],
    interests: [],
    career_goals: ''
  });

  const educationLevels = [
    '10th Standard',
    '12th Standard / Intermediate',
    'Bachelor\'s Degree (Pursuing)',
    'Bachelor\'s Degree (Completed)',
    'Master\'s Degree (Pursuing)',
    'Master\'s Degree (Completed)',
    'Diploma / Vocational Course'
  ];

  const boards = [
    'CBSE',
    'ICSE',
    'State Board',
    'IB (International Baccalaureate)',
    'IGCSE',
    'Open School'
  ];

  const streams = [
    'Science (PCM)',
    'Science (PCB)',
    'Commerce',
    'Humanities/Arts',
    'Vocational',
    'Not Applicable'
  ];

  const subjectOptions = [
    'Physics', 'Chemistry', 'Mathematics', 'Biology',
    'Computer Science', 'Economics', 'Business Studies',
    'Accountancy', 'English', 'History', 'Geography',
    'Political Science', 'Psychology', 'Sociology',
    'Physical Education', 'Fine Arts', 'Other'
  ];

  const interestOptions = [
    'Technology & Programming',
    'Medicine & Healthcare',
    'Business & Management',
    'Arts & Design',
    'Engineering & Innovation',
    'Teaching & Education',
    'Law & Governance',
    'Finance & Banking',
    'Research & Science',
    'Media & Communication',
    'Sports & Fitness',
    'Social Work & NGO',
    'Agriculture & Environment',
    'Defense & Armed Forces'
  ];

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.education_level) {
          toast.error('Please select your education level');
          return false;
        }
        break;
      case 2:
        if (!formData.board_stream) {
          toast.error('Please select your board or stream');
          return false;
        }
        break;
      case 3:
        if (formData.subjects.length === 0) {
          toast.error('Please select at least one subject');
          return false;
        }
        break;
      case 4:
        if (formData.interests.length === 0) {
          toast.error('Please select at least one interest');
          return false;
        }
        break;
      case 5:
        if (!formData.career_goals.trim()) {
          toast.error('Please share your career goals');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setLoading(true);
    startProgressAnimation();
    try {
      const response = await axios.post(`${API}/assessments`, {
        data: formData
      });
      stopProgressAnimation();
      await new Promise(r => setTimeout(r, 600));
      toast.success('Analysis complete! Viewing your career paths...');
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      stopProgressAnimation();
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Daily limit reached! Explore Ask O4S.AI for unlimited career guidance.', { duration: 8000 });
      } else {
        toast.error('Failed to process assessment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    } else {
      return [...array, item];
    }
  };

  const getLoadingMessages = () => [
    'Reading your academic profile...',
    'Analyzing your subject strengths...',
    'Mapping interest areas to careers...',
    'Evaluating market demand in India...',
    'Identifying best-fit career paths...',
    'Calculating success rates & salaries...',
    'Building education roadmaps...',
    'Finding job opportunities...',
    'Crafting your personalized career report...',
    'Almost there — polishing recommendations...',
  ];

  const getCareerQuotes = () => [
    "The future belongs to those who believe in the beauty of their dreams. — Eleanor Roosevelt",
    "Education is the most powerful weapon which you can use to change the world. — Nelson Mandela",
    "Success is not the key to happiness. Happiness is the key to success. — Albert Schweitzer",
    "Your career is your business. It's time for you to manage it as a CEO. — Dorit Sher",
    "The only way to do great work is to love what you do. — Steve Jobs",
    "In the middle of difficulty lies opportunity. — Albert Einstein",
    "An investment in knowledge pays the best interest. — Benjamin Franklin",
    "Don't let what you cannot do interfere with what you can do. — John Wooden",
  ];

  const startProgressAnimation = () => {
    setLoadProgress(0);
    setLoadMsgIndex(0);
    let p = 0;
    const messages = getLoadingMessages();
    progressTimerRef.current = setInterval(() => {
      p += (90 - p) * 0.05 + 0.4;
      if (p > 90) p = 90;
      setLoadProgress(Math.round(p));
    }, 500);
    let mi = 0;
    msgTimerRef.current = setInterval(() => {
      mi = (mi + 1) % messages.length;
      setLoadMsgIndex(mi);
    }, 2500);
  };

  const stopProgressAnimation = () => {
    clearInterval(progressTimerRef.current);
    clearInterval(msgTimerRef.current);
    setLoadProgress(100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 lg:px-12 py-4 border-b border-earth-200">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-earth-700">O4S.ai</h1>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="browse-faqs-btn"
              variant="ghost"
              onClick={() => navigate('/faqs')}
            >
              Ask O4S.AI
            </Button>
            <Button
              data-testid="exit-assessment-btn"
              variant="ghost"
              onClick={() => navigate('/dashboard')}
            >
              Exit
            </Button>
          </div>
        </div>
      </nav>

      {/* Progress Bar */}
      <div className="bg-white border-b border-earth-200 px-4 md:px-8 lg:px-12 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`progress-step ${
                    step < currentStep ? 'completed' : step === currentStep ? 'active' : ''
                  }`}
                >
                  {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
                </div>
                {step < 5 && (
                  <div
                    className={`h-1 w-12 md:w-20 mx-2 ${
                      step < currentStep ? 'bg-accent' : 'bg-earth-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-earth-600">
            Step {currentStep} of 5
          </p>
        </div>
      </div>

      {/* Form Content */}
      <div className="px-4 md:px-8 lg:px-12 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 animate-fade-in">

            {/* Loading State with Progress + Quotes */}
            {loading && (() => {
              const messages = getLoadingMessages();
              const quotes = getCareerQuotes();
              const currentMsg = messages[loadMsgIndex % messages.length];
              const currentQuote = quotes[Math.floor(loadMsgIndex / 2) % quotes.length];
              return (
                <div data-testid="assessment-loading" className="py-12 animate-fade-in max-w-md mx-auto">
                  <div className="relative w-24 h-24 mx-auto mb-8">
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                      <circle cx="48" cy="48" r="42" fill="none" stroke="#E8E0D8" strokeWidth="6" />
                      <circle cx="48" cy="48" r="42" fill="none" stroke="#D95A41" strokeWidth="6"
                        strokeDasharray={`${2 * Math.PI * 42}`}
                        strokeDashoffset={`${2 * Math.PI * 42 * (1 - loadProgress / 100)}`}
                        strokeLinecap="round"
                        className="transition-all duration-500" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-primary">{loadProgress}%</span>
                    </div>
                  </div>

                  <div className="w-full bg-earth-100 rounded-full h-2.5 mb-6 overflow-hidden">
                    <div className="h-2.5 rounded-full bg-gradient-to-r from-primary via-accent to-primary transition-all duration-500 ease-out"
                      style={{ width: `${loadProgress}%` }} />
                  </div>

                  <div className="text-center mb-6">
                    <p className="text-base font-semibold text-earth-700 transition-opacity duration-300" key={currentMsg}>
                      {currentMsg}
                    </p>
                    <p className="text-xs text-earth-400 mt-1">
                      {loadProgress < 30 ? 'Getting started...' : loadProgress < 60 ? 'Almost halfway...' : loadProgress < 90 ? 'Finishing up...' : 'Wrapping your report...'}
                    </p>
                  </div>

                  <div className="bg-surface-alt rounded-xl p-5 border border-earth-200 text-center">
                    <Sparkles className="w-4 h-4 text-accent mx-auto mb-2" />
                    <p className="text-sm text-earth-600 italic leading-relaxed transition-opacity duration-300" key={currentQuote}>
                      "{currentQuote}"
                    </p>
                  </div>

                  <p className="text-earth-400 text-xs mt-6 text-center">This usually takes 20-40 seconds</p>
                </div>
              );
            })()}

            {/* Step 1: Education Level */}
            {!loading && currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-2">What's your current education level?</h2>
                  <p className="text-earth-600">This helps us understand where you are in your journey</p>
                </div>
                <div className="space-y-3">
                  {educationLevels.map((level) => (
                    <button
                      key={level}
                      data-testid={`education-level-${level.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setFormData({ ...formData, education_level: level })}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        formData.education_level === level
                          ? 'border-primary bg-surface-alt'
                          : 'border-earth-200 hover:border-earth-300'
                      }`}
                    >
                      <span className="font-medium">{level}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Board & Stream */}
            {!loading && currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-2">Board & Stream</h2>
                  <p className="text-earth-600">Select your educational board and stream</p>
                </div>
                <div className="space-y-6">
                  <div>
                    <Label className="text-lg mb-3 block">Board</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {boards.map((board) => (
                        <button
                          key={board}
                          data-testid={`board-${board.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setFormData({ ...formData, board_stream: board })}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.board_stream === board
                              ? 'border-primary bg-surface-alt'
                              : 'border-earth-200 hover:border-earth-300'
                          }`}
                        >
                          <span className="font-medium">{board}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-lg mb-3 block">Stream (if applicable)</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {streams.map((stream) => (
                        <button
                          key={stream}
                          data-testid={`stream-${stream.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setFormData({ ...formData, board_stream: `${formData.board_stream} - ${stream}` })}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.board_stream.includes(stream)
                              ? 'border-primary bg-surface-alt'
                              : 'border-earth-200 hover:border-earth-300'
                          }`}
                        >
                          <span className="font-medium text-sm">{stream}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Subjects */}
            {!loading && currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-2">What subjects are you studying?</h2>
                  <p className="text-earth-600">Select all subjects that apply</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {subjectOptions.map((subject) => (
                    <label
                      key={subject}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.subjects.includes(subject)
                          ? 'border-primary bg-surface-alt'
                          : 'border-earth-200 hover:border-earth-300'
                      }`}
                    >
                      <Checkbox
                        data-testid={`subject-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                        checked={formData.subjects.includes(subject)}
                        onCheckedChange={() =>
                          setFormData({
                            ...formData,
                            subjects: toggleArrayItem(formData.subjects, subject)
                          })
                        }
                      />
                      <span className="font-medium text-sm">{subject}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Interests */}
            {!loading && currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-2">What are your interests?</h2>
                  <p className="text-earth-600">Choose fields that excite you</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {interestOptions.map((interest) => (
                    <label
                      key={interest}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.interests.includes(interest)
                          ? 'border-primary bg-surface-alt'
                          : 'border-earth-200 hover:border-earth-300'
                      }`}
                    >
                      <Checkbox
                        data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                        checked={formData.interests.includes(interest)}
                        onCheckedChange={() =>
                          setFormData({
                            ...formData,
                            interests: toggleArrayItem(formData.interests, interest)
                          })
                        }
                      />
                      <span className="font-medium text-sm">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 5: Career Goals */}
            {!loading && currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-2">Tell us about your career goals</h2>
                  <p className="text-earth-600">What do you want to achieve in your career?</p>
                </div>
                <div className="space-y-4">
                  <Textarea
                    data-testid="career-goals-textarea"
                    placeholder="E.g., I want to become a software engineer at a top tech company, or I want to work in healthcare helping people..."
                    value={formData.career_goals}
                    onChange={(e) => setFormData({ ...formData, career_goals: e.target.value })}
                    rows={6}
                    className="resize-none"
                  />
                  <p className="text-sm text-earth-500">Be as specific as possible to get better recommendations</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            {!loading && (
              <div className="flex justify-between items-center mt-12 pt-8 border-t border-earth-200">
                {currentStep > 1 ? (
                  <Button
                    data-testid="step-back-btn"
                    variant="outline"
                    onClick={handleBack}
                    className="h-12 px-6"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" /> Back
                  </Button>
                ) : (
                  <div />
                )}
                
                {currentStep < 5 ? (
                  <Button
                    data-testid="step-next-btn"
                    onClick={handleNext}
                    className="btn-primary h-12 px-8"
                  >
                    Next <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <Button
                    data-testid="submit-assessment-btn"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="btn-primary h-12 px-8"
                  >
                    Get My Career Paths
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;
